import { Component } from 'react';
import { ulid, monotonicFactory } from 'ulid';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

interface State {
    count: number;
    monotonic: boolean;
    timestamp: string;
    output: string;
    copied: boolean;
}

export default class UlidGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private mono = monotonicFactory();
    state: State = {
        count: 5,
        monotonic: false,
        timestamp: '',
        output: '',
        copied: false,
    };

    componentDidMount() {
        this.generate();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    generate = () => {
        const ids: string[] = [];
        const ts = this.state.timestamp.trim() ? Number(this.state.timestamp.trim()) : undefined;
        try {
            for (let i = 0; i < this.state.count; i++) {
                ids.push(this.state.monotonic ? this.mono(ts) : ulid(ts));
            }
            this.setState({ output: ids.join('\n'), copied: false });
        } catch (e: any) {
            this.setState({ output: 'Error: ' + e.message, copied: false });
        }
    };

    copyOutput = () => {
        if (!this.state.output || this.state.output.startsWith('Error')) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { count, monotonic, timestamp, output, copied } = this.state;
        const idCount = output ? output.split('\n').filter(Boolean).length : 0;

        const form = (
            <div className={genStyles.section}>
                <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                <div className={genStyles.fieldRow}>
                    <div className={genStyles.field}>
                        <label htmlFor="ulid-count">Number of IDs</label>
                        <input id="ulid-count" type="number" inputMode="decimal" min={1} max={1000} value={count}
                            onChange={e => this.setState({ count: Math.max(1, Math.min(1000, +e.target.value || 5)) }, this.generate)} />
                    </div>
                </div>
                <div className={genStyles.field}>
                    <label htmlFor="ulid-ts">Timestamp (epoch ms, blank = now)</label>
                    <input id="ulid-ts" type="text" value={timestamp}
                        placeholder="e.g. 1700000000000"
                        onChange={e => this.setState({ timestamp: e.target.value }, this.generate)} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={monotonic}
                        onChange={e => this.setState({ monotonic: e.target.checked }, this.generate)} />
                    Monotonic mode (guarantees sort order within same ms)
                </label>
                <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate} style={{ alignSelf: 'flex-start' }}>
                    ↻ Regenerate
                </button>
                <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>
                    ULID = 10 chars timestamp (48-bit) + 16 chars randomness (80-bit). Lexicographically sortable. Crockford base32 (no I, L, O, U).
                </p>
            </div>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated ULIDs"
                previewMeta={`${idCount} ULID${idCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated ULIDs">{output}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy all'}
                    </button>
                }
            />
        );
    }
}
